import React, { useContext, useState, useEffect, useMemo } from "react";
import { DataContext } from "../contexts/DataContext";
import { useAuth } from "../contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import logo from "../logo-wis.avif";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSpinner,
  faEye,
  faSync,
  faSearch,
  faFilter,
  faFileExcel,
  faCalendarAlt,
  faTimes,
  faChevronDown,
  faChevronUp,
  faUser,
  faSignOutAlt,
  faQuestionCircle,
  faBell,
  faCog,
  faEnvelope,
  faShip,
  faTruck,
  faPlane,
  faQuestion,
  faFileUpload,
  faChartLine,
  faHandshake,
  faBuilding,
} from "@fortawesome/free-solid-svg-icons";

// Import Subcomponents
import TrafficFilters from "./TrafficFilters";
import TrafficSearchBar from "./TrafficSearchBar";
import TrafficMiniDashboard from "./TrafficMiniDashboard";
import TrafficGridControlTower from "./TrafficGridControlTower";
import TrafficDetails from "./TrafficDetailsControlTower";
import Pagination from "../utils/Pagination";
import ExportCSVButton from "../utils/ExportCSVButton";
import ActiveFiltersDisplay from "./ActiveFiltersDisplay";
import EnhancedSearchAndFilterBar from "./EnhancedSearchAndFilterBar";
import GlobalHeader from "./GlobalHeader";

// Import the new Traffic Scheduler component
import TrafficScheduler from "./TrafficScheduler";

// Import the new Schedule Program component
import ScheduleProgram from "./ScheduleProgram";

const TrafficComponent = () => {
  const { trafficData, isLoading, fetchTrafficData, setShouldFetchData } =
    useContext(DataContext);
  const { logout } = useAuth();
  const navigate = useNavigate();

  // State management
  const [filteredData, setFilteredData] = useState([]);
  const [expandedRows, setExpandedRows] = useState([]);
  const [selectedItem, setSelectedItem] = useState(null);
  const [showDetailsPanel, setShowDetailsPanel] = useState(false);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [language, setLanguage] = useState("en");
  const [fileUploading, setFileUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  // Active tab state - Add a new state for the active tab
  const [activeTab, setActiveTab] = useState("shipments"); // 'shipments', 'scheduler', or 'schedule-program'

  // Filter states - Same as original
  const [selectedCustomers, setSelectedCustomers] = useState([]);
  const [selectedDivisions, setSelectedDivisions] = useState([]);
  const [selectedRfcs, setSelectedRfcs] = useState([]);
  const [selectedOperationTypes, setSelectedOperationTypes] = useState([]);
  const [selectedEntryPoints, setSelectedEntryPoints] = useState([]);
  const [selectedStatus, setSelectedStatus] = useState([]);
  const [selectedPedimentos, setSelectedPedimentos] = useState([]);
  
  // New filter states for additional columns
  const [selectedPurchaseOrders, setSelectedPurchaseOrders] = useState([]);
  const [selectedBillOfLadings, setSelectedBillOfLadings] = useState([]);
  const [selectedClavePeds, setSelectedClavePeds] = useState([]);
  const [selectedRegimes, setSelectedRegimes] = useState([]);
  const [selectedTrafficStatus, setSelectedTrafficStatus] = useState([]);
  const [selectedSuppliers, setSelectedSuppliers] = useState([]);
  const [weightRange, setWeightRange] = useState({ min: "", max: "" });
  const [packagesRange, setPackagesRange] = useState({ min: "", max: "" });
  
  const [dateRange, setDateRange] = useState([
    {
      startDate: null,
      endDate: null,
      key: "selection",
    },
  ]);

  // Filter options - Same as original
  const [customerOptions, setCustomerOptions] = useState([]);
  const [divisionOptions, setDivisionOptions] = useState([]);
  const [rfcOptions, setRfcOptions] = useState([]);
  const [operationTypeOptions, setOperationTypeOptions] = useState([]);
  const [entryPointOptions, setEntryPointOptions] = useState([]);
  const [pedimentoOptions, setPedimentoOptions] = useState([]);
  
  // New filter options for additional columns
  const [purchaseOrderOptions, setPurchaseOrderOptions] = useState([]);
  const [billOfLadingOptions, setBillOfLadingOptions] = useState([]);
  const [clavePedOptions, setClavePedOptions] = useState([]);
  const [regimeOptions, setRegimeOptions] = useState([]);
  const [trafficStatusOptions, setTrafficStatusOptions] = useState([]);
  const [supplierOptions, setSupplierOptions] = useState([]);
  const [weightStats, setWeightStats] = useState({ min: 0, max: 0 });
  const [packagesStats, setPackagesStats] = useState({ min: 0, max: 0 });
  
  const statusOptions = [
    { value: "Paid", label: "Paid" },
    { value: "Open", label: "Open" },
  ];

  // Sorting
  const [sortConfig, setSortConfig] = useState({
    key: "detail.dates_data.date_init",
    direction: "desc",
  });

  // Pagination state
  const [pagination, setPagination] = useState({
    currentPage: 1,
    pageSize: 100,
    totalPages: 1,
    totalItems: 0,
  });

  // Trigger data fetch when component mounts
  useEffect(() => {
    setShouldFetchData((prev) => ({ ...prev, fetchTrafficData: true }));

    return () => {
      setShouldFetchData((prev) => ({ ...prev, fetchTrafficData: false }));
    };
  }, [setShouldFetchData]);

  useEffect(() => {
    fetchTrafficData();
  }, [fetchTrafficData]);

  // Process the data once it's loaded - Same as original
  useEffect(() => {
    if (trafficData) {
      // Extract filter options from data
      const uniqueCustomers = Array.from(
        new Set(trafficData.map((item) => item.header.customer_name))
      ).map((customer) => ({ value: customer, label: customer }));
      setCustomerOptions(uniqueCustomers);

      const uniqueDivisions = Array.from(
        new Set(trafficData.map((item) => item.header.customer_division))
      ).map((division) => ({ value: division, label: division }));
      setDivisionOptions(uniqueDivisions);

      const uniqueRfcs = Array.from(
        new Set(trafficData.map((item) => item.header.customer_rfc))
      ).map((rfc) => ({ value: rfc, label: rfc }));
      setRfcOptions(uniqueRfcs);

      const uniqueOperationTypes = Array.from(
        new Set(trafficData.map((item) => item.header.operation_type))
      ).map((operationType) => ({
        value: operationType,
        label: getOperationTypeText(operationType),
      }));
      setOperationTypeOptions(uniqueOperationTypes);

      const uniqueEntryPoints = Array.from(
        new Set(
          trafficData.map((item) => item.header.pedimento.pedimento_entry_point)
        )
      ).map((entryPoint) => ({ value: entryPoint, label: entryPoint }));
      setEntryPointOptions(uniqueEntryPoints);

      const uniquePedimentos = Array.from(
        new Set(
          trafficData
            .map((item) => item.header.pedimento.pedimento_number)
            .filter(Boolean)
        )
      ).map((pedimento) => ({ value: pedimento, label: pedimento }));
      setPedimentoOptions(uniquePedimentos);

      // Extract new filter options
      const uniquePurchaseOrders = Array.from(
        new Set(
          trafficData
            .map((item) => item.header.purchase_order)
            .filter(Boolean)
        )
      ).map((po) => ({ value: po, label: po }));
      setPurchaseOrderOptions(uniquePurchaseOrders);

      const uniqueBillOfLadings = Array.from(
        new Set(
          trafficData
            .map((item) => 
              item.header.bill_of_lading_data && item.header.bill_of_lading_data[0]
                ? item.header.bill_of_lading_data[0].bill_of_lading
                : null
            )
            .filter(Boolean)
        )
      ).map((bol) => ({ value: bol, label: bol }));
      setBillOfLadingOptions(uniqueBillOfLadings);

      const uniqueClavePeds = Array.from(
        new Set(
          trafficData
            .map((item) => item.header.detail?.traffic_data?.clave_ped)
            .filter(Boolean)
        )
      ).map((clave) => ({ value: clave, label: clave }));
      setClavePedOptions(uniqueClavePeds);

      const uniqueRegimes = Array.from(
        new Set(
          trafficData
            .map((item) => item.header.detail?.traffic_data?.regime)
            .filter(Boolean)
        )
      ).map((regime) => ({ value: regime, label: regime }));
      setRegimeOptions(uniqueRegimes);

      const uniqueTrafficStatus = Array.from(
        new Set(
          trafficData
            .map((item) => item.header.trafficStatus)
            .filter(Boolean)
        )
      ).map((status) => ({ value: status, label: status }));
      setTrafficStatusOptions(uniqueTrafficStatus);

      const uniqueSuppliers = Array.from(
        new Set(
          trafficData
            .map((item) => item.header?.detail?.invoice_data?.[0]?.supplier)
            .filter(Boolean)
        )
      ).map((supplier) => ({ value: supplier, label: supplier }));
      setSupplierOptions(uniqueSuppliers);

      // Calculate weight and packages statistics
      const weights = trafficData
        .map((item) => parseFloat(item.header.weight))
        .filter((weight) => !isNaN(weight) && weight > 0);
      
      const packages = trafficData
        .map((item) => parseInt(item.header.total_packages))
        .filter((pkg) => !isNaN(pkg) && pkg > 0);

      if (weights.length > 0) {
        setWeightStats({
          min: Math.min(...weights),
          max: Math.max(...weights)
        });
      }

      if (packages.length > 0) {
        setPackagesStats({
          min: Math.min(...packages),
          max: Math.max(...packages)
        });
      }
    }
  }, [trafficData]);

  // Apply filters to data - FIXED FILTERING LOGIC
  useEffect(() => {
    let filtered = trafficData || [];

    // Apply search filter
    if (searchTerm) {
      filtered = filtered.filter((item) => {
        const searchLower = searchTerm.toLowerCase();
        const billOfLading = item.header.bill_of_lading_data && item.header.bill_of_lading_data[0]
          ? item.header.bill_of_lading_data[0].bill_of_lading
          : '';
        return (
          item.header.reference?.toLowerCase().includes(searchLower) ||
          item.header.customer_name?.toLowerCase().includes(searchLower) ||
          item.header.customer_rfc?.toLowerCase().includes(searchLower) ||
          item.header.pedimento.pedimento_number
            ?.toLowerCase()
            .includes(searchLower) ||
          item.header.trailer_number?.toLowerCase().includes(searchLower) ||
          item.header.purchase_order?.toLowerCase().includes(searchLower) ||
          billOfLading?.toLowerCase().includes(searchLower) ||
          item.header.total_packages?.toString().includes(searchLower) ||
          item.header.weight?.toString().includes(searchLower) ||
          item.header.detail?.traffic_data?.clave_ped?.toLowerCase().includes(searchLower) ||
          item.header.detail?.traffic_data?.regime?.toLowerCase().includes(searchLower) ||
          item.header.trafficStatus?.toLowerCase().includes(searchLower)
        );
      });
    }

    // Apply customer filter
    if (selectedCustomers.length > 0) {
      const customerValues = selectedCustomers.map((c) => c.value);
      filtered = filtered.filter((item) =>
        customerValues.includes(item.header.customer_name)
      );
    }

    // Apply division filter
    if (selectedDivisions.length > 0) {
      const divisionValues = selectedDivisions.map((d) => d.value);
      filtered = filtered.filter((item) =>
        divisionValues.includes(item.header.customer_division)
      );
    }

    // Apply RFC filter
    if (selectedRfcs.length > 0) {
      const rfcValues = selectedRfcs.map((r) => r.value);
      filtered = filtered.filter((item) =>
        rfcValues.includes(item.header.customer_rfc)
      );
    }

    // Apply operation type filter
    if (selectedOperationTypes.length > 0) {
      const operationTypeValues = selectedOperationTypes.map((o) => o.value);
      filtered = filtered.filter((item) =>
        operationTypeValues.includes(item.header.operation_type)
      );
    }

    // Apply entry point filter
    if (selectedEntryPoints.length > 0) {
      const entryPointValues = selectedEntryPoints.map((e) => e.value);
      filtered = filtered.filter((item) =>
        entryPointValues.includes(item.header.pedimento.pedimento_entry_point)
      );
    }

    // Apply pedimento filter
    if (selectedPedimentos.length > 0) {
      const pedimentoValues = selectedPedimentos.map((p) => p.value);
      filtered = filtered.filter(
        (item) =>
          item.header.pedimento.pedimento_number &&
          pedimentoValues.includes(item.header.pedimento.pedimento_number)
      );
    }

    // Apply status filter
    if (selectedStatus.length > 0) {
      const statusValues = selectedStatus.map((s) => s.value);
      filtered = filtered.filter((item) => {
        const trafficStatus = item.header.detail?.traffic_data?.status;
        // Map the actual status values to "Paid" or "Open" based on your business logic
        // This is an example - adjust based on your actual status mapping
        if (statusValues.includes("Paid") && trafficStatus === "Pagado") {
          return true;
        }
        if (statusValues.includes("Open") && trafficStatus !== "Pagado") {
          return true;
        }
        return false;
      });
    }

    // Apply date range filter
    if (dateRange[0].startDate && dateRange[0].endDate) {
      filtered = filtered.filter((item) => {
        const itemDate = new Date(item.header.detail?.dates_data?.date_init);
        return (
          itemDate >= dateRange[0].startDate && itemDate <= dateRange[0].endDate
        );
      });
    }

    // Apply new filters
    // Apply purchase order filter
    if (selectedPurchaseOrders.length > 0) {
      const purchaseOrderValues = selectedPurchaseOrders.map((po) => po.value);
      filtered = filtered.filter((item) =>
        purchaseOrderValues.includes(item.header.purchase_order)
      );
    }

    // Apply bill of lading filter
    if (selectedBillOfLadings.length > 0) {
      const billOfLadingValues = selectedBillOfLadings.map((bol) => bol.value);
      filtered = filtered.filter((item) => {
        const billOfLading = item.header.bill_of_lading_data && item.header.bill_of_lading_data[0]
          ? item.header.bill_of_lading_data[0].bill_of_lading
          : null;
        return billOfLading && billOfLadingValues.includes(billOfLading);
      });
    }

    // Apply clave ped filter
    if (selectedClavePeds.length > 0) {
      const clavePedValues = selectedClavePeds.map((cp) => cp.value);
      filtered = filtered.filter((item) =>
        clavePedValues.includes(item.header.detail?.traffic_data?.clave_ped)
      );
    }

    // Apply regime filter
    if (selectedRegimes.length > 0) {
      const regimeValues = selectedRegimes.map((r) => r.value);
      filtered = filtered.filter((item) =>
        regimeValues.includes(item.header.detail?.traffic_data?.regime)
      );
    }

    // Apply supplier filter
    if (selectedSuppliers.length > 0) {
      const supplierValues = selectedSuppliers.map((s) => s.value);
      filtered = filtered.filter((item) =>
        supplierValues.includes(item.header?.detail?.invoice_data?.[0]?.supplier)
      );
    }

    // Apply traffic status filter
    if (selectedTrafficStatus.length > 0) {
      const trafficStatusValues = selectedTrafficStatus.map((ts) => ts.value);
      filtered = filtered.filter((item) =>
        trafficStatusValues.includes(item.header.trafficStatus)
      );
    }

    // Apply weight range filter
    if (weightRange.min !== "" || weightRange.max !== "") {
      filtered = filtered.filter((item) => {
        const weight = parseFloat(item.header.weight);
        if (isNaN(weight)) return false;
        
        const minWeight = weightRange.min !== "" ? parseFloat(weightRange.min) : 0;
        const maxWeight = weightRange.max !== "" ? parseFloat(weightRange.max) : Infinity;
        
        return weight >= minWeight && weight <= maxWeight;
      });
    }

    // Apply packages range filter
    if (packagesRange.min !== "" || packagesRange.max !== "") {
      filtered = filtered.filter((item) => {
        const packages = parseInt(item.header.total_packages);
        if (isNaN(packages)) return false;
        
        const minPackages = packagesRange.min !== "" ? parseInt(packagesRange.min) : 0;
        const maxPackages = packagesRange.max !== "" ? parseInt(packagesRange.max) : Infinity;
        
        return packages >= minPackages && packages <= maxPackages;
      });
    }

    setFilteredData(filtered);

    // Update pagination
    const totalPages = Math.ceil(filtered.length / pagination.pageSize);
    setPagination((prev) => ({
      ...prev,
      totalPages: totalPages,
      totalItems: filtered.length,
      // Reset to page 1 if current page is out of bounds
      currentPage: prev.currentPage > totalPages ? 1 : prev.currentPage,
    }));
  }, [
    trafficData,
    searchTerm,
    selectedCustomers,
    selectedDivisions,
    selectedRfcs,
    selectedOperationTypes,
    selectedEntryPoints,
    selectedPedimentos,
    selectedStatus,
    selectedPurchaseOrders,
    selectedBillOfLadings,
    selectedClavePeds,
    selectedRegimes,
    selectedTrafficStatus,
    selectedSuppliers,
    weightRange,
    packagesRange,
    dateRange,
    pagination.pageSize,
  ]);

  // Various helper functions from the original component
  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const getOperationTypeText = (operationType) => {
    if (operationType === "1") return "Import";
    if (operationType === "2") return "Export";
    return operationType;
  };

  const getTransportIcon = (entryPoint) => {
    const transportIconsMap = {
      // Seafreight
      160: { icon: faShip, color: "text-blue-600", title: "Sea Freight" },
      510: { icon: faShip, color: "text-blue-600", title: "Sea Freight" },
      430: { icon: faShip, color: "text-blue-600", title: "Sea Freight" },

      // Roadfreight
      240: { icon: faTruck, color: "text-green-600", title: "Road Freight" },

      // Airfreight
      520: { icon: faPlane, color: "text-purple-600", title: "Air Freight" },
      521: { icon: faPlane, color: "text-purple-600", title: "Air Freight" },
      640: { icon: faPlane, color: "text-purple-600", title: "Air Freight" },

      // Default for unknown entry points
      default: {
        icon: faQuestion,
        color: "text-gray-500",
        title: "Unknown Transport Type",
      },
    };

    return transportIconsMap[entryPoint] || transportIconsMap["default"];
  };

  const clearAllFilters = () => {
    setSelectedCustomers([]);
    setSelectedDivisions([]);
    setSelectedRfcs([]);
    setSelectedOperationTypes([]);
    setSelectedEntryPoints([]);
    setSelectedPedimentos([]);
    setSelectedStatus([]);
    setSelectedPurchaseOrders([]);
    setSelectedBillOfLadings([]);
    setSelectedClavePeds([]);
    setSelectedRegimes([]);
    setSelectedTrafficStatus([]);
    setSelectedSuppliers([]);
    setWeightRange({ min: "", max: "" });
    setPackagesRange({ min: "", max: "" });
    setSearchTerm("");
    setDateRange([
      {
        startDate: null,
        endDate: null,
        key: "selection",
      },
    ]);
  };

  const clearFilterType = (filterType) => {
    switch (filterType) {
      case "customers":
        setSelectedCustomers([]);
        break;
      case "divisions":
        setSelectedDivisions([]);
        break;
      case "rfcs":
        setSelectedRfcs([]);
        break;
      case "operationTypes":
        setSelectedOperationTypes([]);
        break;
      case "entryPoints":
        setSelectedEntryPoints([]);
        break;
      case "pedimentos":
        setSelectedPedimentos([]);
        break;
      case "status":
        setSelectedStatus([]);
        break;
      case "purchaseOrders":
        setSelectedPurchaseOrders([]);
        break;
      case "billOfLadings":
        setSelectedBillOfLadings([]);
        break;
      case "clavePeds":
        setSelectedClavePeds([]);
        break;
      case "regimes":
        setSelectedRegimes([]);
        break;
      case "trafficStatus":
        setSelectedTrafficStatus([]);
        break;
      case "suppliers":
        setSelectedSuppliers([]);
        break;
      case "weightRange":
        setWeightRange({ min: "", max: "" });
        break;
      case "packagesRange":
        setPackagesRange({ min: "", max: "" });
        break;
      case "search":
        setSearchTerm("");
        break;
      case "dateRange":
        setDateRange([
          {
            startDate: null,
            endDate: null,
            key: "selection",
          },
        ]);
        break;
      default:
        break;
    }
  };

  // Request sort by column
  const requestSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key) {
      direction = sortConfig.direction === "asc" ? "desc" : "asc";
    }
    setSortConfig({ key, direction });
  };

  // Handle row expansion
  const handleRowClick = (reference) => {
    const isRowCurrentlyExpanded = expandedRows.includes(reference);
    const newExpandedRows = isRowCurrentlyExpanded
      ? expandedRows.filter((id) => id !== reference)
      : [...expandedRows, reference];
    setExpandedRows(newExpandedRows);
  };

  // View details in panel
  const handleViewDetails = (item) => {
    setSelectedItem(item);
    setShowDetailsPanel(true);
  };

  // Handle page change in pagination
  const handlePageChange = (page) => {
    setPagination((prev) => ({
      ...prev,
      currentPage: page,
    }));
  };

  // Handle page size change
  const handlePageSizeChange = (size) => {
    setPagination((prev) => ({
      ...prev,
      pageSize: size,
      // Don't need to reset currentPage here as your Pagination component handles it
    }));
    // Reset expanded rows when changing page size
    setExpandedRows([]);
  };

  // Handle file upload
  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append("file", file);

    setFileUploading(true);
    setUploadProgress(0);

    try {
      // Simulating file upload - in a real implementation, use axios post
      // This is just for UI demonstration purposes
      const simulateUpload = () => {
        let progress = 0;
        const interval = setInterval(() => {
          progress += 10;
          setUploadProgress(progress);
          if (progress >= 100) {
            clearInterval(interval);
            setFileUploading(false);
            // Refresh data
            fetchTrafficData();
          }
        }, 300);
      };

      simulateUpload();
    } catch (err) {
      console.error("Error uploading file:", err);
      setFileUploading(false);
    }
  };

  // Sort data based on current sort config - Fixed with proper nested property access
  const sortedData = useMemo(() => {
    if (!sortConfig.key) return filteredData;

    return [...filteredData].sort((a, b) => {
      let aValue, bValue;

      const getFieldValue = (item, keyPath) => {
        const keys = keyPath.split(".");
        let value = item.header;

        // Handle special case for bill of lading nested array
        if (keyPath === "bill_of_lading_data.0.bill_of_lading") {
          return item.header.bill_of_lading_data && item.header.bill_of_lading_data[0]
            ? item.header.bill_of_lading_data[0].bill_of_lading || ""
            : "";
        }

        for (const key of keys) {
          value = value?.[key];
          if (value === undefined || value === null) {
            return "";
          }
        }

        return value;
      };

      aValue = getFieldValue(a, sortConfig.key);
      bValue = getFieldValue(b, sortConfig.key);

      // Handle specific fields (dates, numbers)
      if (sortConfig.key === "detail.dates_data.date_init") {
        aValue = aValue ? new Date(aValue) : new Date(0);
        bValue = bValue ? new Date(bValue) : new Date(0);
      } else if (sortConfig.key === "weight" || sortConfig.key === "total_packages") {
        // Handle numeric fields
        aValue = parseFloat(aValue) || 0;
        bValue = parseFloat(bValue) || 0;
      } else if (typeof aValue === "string" && typeof bValue === "string") {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }

      if (aValue < bValue) return sortConfig.direction === "asc" ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === "asc" ? 1 : -1;
      return 0;
    });
  }, [filteredData, sortConfig]);

  // Calculate paginated subset of data
  const paginatedData = useMemo(() => {
    const start = (pagination.currentPage - 1) * pagination.pageSize;
    const end = start + pagination.pageSize;
    return sortedData.slice(start, end);
  }, [pagination.currentPage, pagination.pageSize, sortedData]);

  // Prepare data for CSV export
  const transformDataForCSV = (data) => {
    return data.map((item) => {
      // Extract basic header fields
      const basicFields = {
        reference: item.header.reference,
        trailer_number: item.header.trailer_number,
        purchase_order: item.header.purchase_order,
        total_packages: item.header.total_packages,
        weight: item.header.weight,
        delivery_type: item.header.delivery_type,
        operation_type: getOperationTypeText(item.header.operation_type),
        customer_name: item.header.customer_name,
        customer_id: item.header.customer_id,
        customer_rfc: item.header.customer_rfc,
        customer_division: item.header.customer_division,
      };

      // Extract pedimento fields
      const pedimentoFields = {
        pedimento_license: item.header.pedimento.pedimento_license,
        pedimento_entry_point: item.header.pedimento.pedimento_entry_point,
        pedimento_number: item.header.pedimento.pedimento_number,
        pedimento_consolidation: item.header.pedimento.pedimento_consolidation,
        pedimento_remesa: item.header.pedimento.pedimento_remesa,
      };

      // Extract date fields
      const datesData = item.header.detail?.dates_data || {};
      const dateFields = {
        date_init: datesData.date_init || "",
        date_inbound: datesData.date_inbound || "",
        date_end: datesData.date_end || "",
        date_init_loading: datesData.date_init_loading || "",
        date_init_unloading: datesData.date_init_unloading || "",
        date_loading_order: datesData.date_loading_order || "",
        date_yard_outbound: datesData.date_yard_outbound || "",
        date_doda: datesData.date_doda || "",
        date_crossing: datesData.date_crossing || "",
        date_pedimento_paid: datesData.date_pedimento_paid || "",
      };

      // Extract traffic data fields
      const trafficData = item.header.detail?.traffic_data || {};
      const trafficFields = {
        status: trafficData.status || "",
        clave_ped: trafficData.clave_ped || "",
        regime: trafficData.regime || "",
        total_taxes: trafficData.total_taxes || "",
        total_taxes_requested_date:
          trafficData.total_taxes_requested_date || "",
        mexican_carrier: trafficData.mexican_carrier || "",
        transport_code: trafficData.transport_code || "",
        mexican_carrier_trailer_number:
          trafficData.mexican_carrier_trailer_number || "",
        us_carrier: trafficData.us_carrier || "",
        us_carrier_trailer_number: trafficData.us_carrier_trailer_number || "",
        cargo: trafficData.cargo || "",
        observations: trafficData.observations || "",
        traffic_observations: trafficData.traffic_observations || "",
        annotations: trafficData.annotations || "",
      };

      // Extract first bill of lading information (if exists)
      const billOfLadingData =
        item.header.bill_of_lading_data && item.header.bill_of_lading_data[0]
          ? {
              bill_of_lading:
                item.header.bill_of_lading_data[0].bill_of_lading || "",
              bill_of_lading_urgent:
                item.header.bill_of_lading_data[0].bill_of_lading_urgent || "",
              date_created_bill_of_lading_urgent:
                item.header.bill_of_lading_data[0]
                  .date_created_bill_of_lading_urgent || "",
              date_arrival_bill_of_lading_urgent:
                item.header.bill_of_lading_data[0]
                  .date_arrival_bill_of_lading_urgent || "",
            }
          : {
              bill_of_lading: "",
              bill_of_lading_urgent: "",
              date_created_bill_of_lading_urgent: "",
              date_arrival_bill_of_lading_urgent: "",
            };

      // Combine all sections into one flat object for CSV export
      return {
        ...basicFields,
        ...pedimentoFields,
        ...dateFields,
        ...trafficFields,
        ...billOfLadingData,
      };
    });
  };

  // Render loading spinner
  if (isLoading && !trafficData) {
    return (
      <div className="flex justify-center items-center h-screen">
        <FontAwesomeIcon
          icon={faSpinner}
          className="text-xl font-semibold animate-spin text-blue-600"
        />
        <div className="text-xl font-semibold ml-2">Loading...</div>
      </div>
    );
  }

  // Main component render
  // Custom action buttons for Traffic Control Tower
  const actionButtons = (
    <>
      <div className="relative">
        <input
          type="file"
          id="fileUpload"
          className="absolute inset-0 opacity-0 w-full cursor-pointer"
          onChange={handleFileUpload}
          accept=".xlsx,.xls,.csv"
          disabled={fileUploading}
        />
        <button
          className="flex items-center space-x-2 bg-blue-600 hover:bg-blue-700 px-4 py-2 rounded-lg text-sm font-medium transition-colors duration-200"
          disabled={fileUploading}
        >
          <FontAwesomeIcon
            icon={fileUploading ? faSpinner : faFileUpload}
            className={fileUploading ? "animate-spin" : ""}
          />
          <span>
            {fileUploading
              ? `Uploading (${uploadProgress}%)`
              : "Import Traffic Data"}
          </span>
        </button>
      </div>
      <button
        onClick={() => fetchTrafficData()}
        className="flex items-center space-x-2 bg-green-600 hover:bg-green-700 px-4 py-2 rounded-lg text-sm font-medium transition-colors duration-200"
        disabled={isLoading}
      >
        <FontAwesomeIcon
          icon={isLoading ? faSpinner : faSync}
          className={isLoading ? "animate-spin" : ""}
        />
        <span>{isLoading ? "Processing..." : "Refresh Data"}</span>
      </button>
    </>
  );

  return (
    <div className="bg-gray-50 min-h-screen flex flex-col">
      {/* GlobalHeader */}
      <GlobalHeader 
        title="Traffic Control Tower"
        icon={faChartLine}
        actionButtons={actionButtons}
      />
      
      {/* Tabs - Updated to include the Scheduler tab */}
      <div className="bg-gradient-to-r from-[#1A2654] to-blue-700">
        <div className="flex px-6 border-b border-blue-600">
          <button
            className={`px-4 py-2 font-medium text-sm rounded-t-lg transition-colors duration-200 ${
              activeTab === "shipments"
                ? "bg-white text-blue-800"
                : "text-blue-100 hover:bg-blue-800"
            }`}
            onClick={() => setActiveTab("shipments")}
          >
            All Shipments
          </button>
          <button
            className={`px-4 py-2 font-medium text-sm rounded-t-lg transition-colors duration-200 ${
              activeTab === "scheduler"
                ? "bg-white text-blue-800"
                : "text-blue-100 hover:bg-blue-800"
            }`}
            onClick={() => setActiveTab("scheduler")}
          >
            Customs Traffic Scheduler
          </button>
          <button
            className={`px-4 py-2 font-medium text-sm rounded-t-lg transition-colors duration-200 ${
              activeTab === "schedule-program"
                ? "bg-white text-blue-800"
                : "text-blue-100 hover:bg-blue-800"
            }`}
            onClick={() => setActiveTab("schedule-program")}
          >
            Schedule Program
          </button>
        </div>
      </div>

      {/* Content area - Conditional rendering based on active tab */}
      {activeTab === "shipments" ? (
        <div className="flex flex-1 overflow-hidden">
          {/* Shipments Tab */}
          {/* Main table and filters */}
          <div className="flex-1 p-6 overflow-y-auto">
              {/* Enhanced Search and Filter Bar */}
              <EnhancedSearchAndFilterBar
                searchTerm={searchTerm}
                setSearchTerm={setSearchTerm}
                setShowFilters={setShowFilters}
                showFilters={showFilters}
                setShowDatePicker={setShowDatePicker}
                showDatePicker={showDatePicker}
                dateRange={dateRange}
                setDateRange={setDateRange}
                clearFilterType={clearFilterType}
                transformDataForCSV={transformDataForCSV}
                filteredData={filteredData}
                ExportCSVButton={ExportCSVButton}
              />

              {/* Enhanced Traffic Filters Component */}
              <div className="mb-6">
                <TrafficFilters
                  customerOptions={customerOptions}
                  divisionOptions={divisionOptions}
                  rfcOptions={rfcOptions}
                  operationTypeOptions={operationTypeOptions}
                  entryPointOptions={entryPointOptions}
                  pedimentoOptions={pedimentoOptions}
                  statusOptions={statusOptions}
                  purchaseOrderOptions={purchaseOrderOptions}
                  billOfLadingOptions={billOfLadingOptions}
                  clavePedOptions={clavePedOptions}
                  regimeOptions={regimeOptions}
                  trafficStatusOptions={trafficStatusOptions}
                  supplierOptions={supplierOptions}
                  weightStats={weightStats}
                  packagesStats={packagesStats}
                  selectedCustomers={selectedCustomers}
                  selectedDivisions={selectedDivisions}
                  selectedRfcs={selectedRfcs}
                  selectedOperationTypes={selectedOperationTypes}
                  selectedEntryPoints={selectedEntryPoints}
                  selectedPedimentos={selectedPedimentos}
                  selectedStatus={selectedStatus}
                  selectedPurchaseOrders={selectedPurchaseOrders}
                  selectedBillOfLadings={selectedBillOfLadings}
                  selectedClavePeds={selectedClavePeds}
                  selectedRegimes={selectedRegimes}
                  selectedTrafficStatus={selectedTrafficStatus}
                  selectedSuppliers={selectedSuppliers}
                  weightRange={weightRange}
                  packagesRange={packagesRange}
                  setSelectedCustomers={setSelectedCustomers}
                  setSelectedDivisions={setSelectedDivisions}
                  setSelectedRfcs={setSelectedRfcs}
                  setSelectedOperationTypes={setSelectedOperationTypes}
                  setSelectedEntryPoints={setSelectedEntryPoints}
                  setSelectedPedimentos={setSelectedPedimentos}
                  setSelectedStatus={setSelectedStatus}
                  setSelectedPurchaseOrders={setSelectedPurchaseOrders}
                  setSelectedBillOfLadings={setSelectedBillOfLadings}
                  setSelectedClavePeds={setSelectedClavePeds}
                  setSelectedRegimes={setSelectedRegimes}
                  setSelectedTrafficStatus={setSelectedTrafficStatus}
                  setSelectedSuppliers={setSelectedSuppliers}
                  setWeightRange={setWeightRange}
                  setPackagesRange={setPackagesRange}
                  clearAllFilters={clearAllFilters}
                  clearFilterType={clearFilterType}
                  showFilters={showFilters}
                  setShowFilters={setShowFilters}
                />
              </div>

              {/* Active Filters Display */}
              {!showFilters && (
                <ActiveFiltersDisplay
                  selectedCustomers={selectedCustomers}
                  selectedDivisions={selectedDivisions}
                  selectedRfcs={selectedRfcs}
                  selectedOperationTypes={selectedOperationTypes}
                  selectedEntryPoints={selectedEntryPoints}
                  selectedPedimentos={selectedPedimentos}
                  selectedStatus={selectedStatus}
                  selectedPurchaseOrders={selectedPurchaseOrders}
                  selectedBillOfLadings={selectedBillOfLadings}
                  selectedClavePeds={selectedClavePeds}
                  selectedRegimes={selectedRegimes}
                  selectedTrafficStatus={selectedTrafficStatus}
                  weightRange={weightRange}
                  packagesRange={packagesRange}
                  dateRange={dateRange}
                  searchTerm={searchTerm}
                  setSelectedCustomers={setSelectedCustomers}
                  setSelectedDivisions={setSelectedDivisions}
                  setSelectedRfcs={setSelectedRfcs}
                  setSelectedOperationTypes={setSelectedOperationTypes}
                  setSelectedEntryPoints={setSelectedEntryPoints}
                  setSelectedPedimentos={setSelectedPedimentos}
                  setSelectedStatus={setSelectedStatus}
                  setSelectedPurchaseOrders={setSelectedPurchaseOrders}
                  setSelectedBillOfLadings={setSelectedBillOfLadings}
                  setSelectedClavePeds={setSelectedClavePeds}
                  setSelectedRegimes={setSelectedRegimes}
                  setSelectedTrafficStatus={setSelectedTrafficStatus}
                  setWeightRange={setWeightRange}
                  setPackagesRange={setPackagesRange}
                  setDateRange={setDateRange}
                  setSearchTerm={setSearchTerm}
                  clearAllFilters={clearAllFilters}
                  clearFilterType={clearFilterType}
                />
              )}

              {/* Traffic Mini Dashboard */}
              <div className="mb-6">
                <TrafficMiniDashboard trafficData={filteredData} />
              </div>

              {/* Data Loading or Error */}
              {isLoading ? (
                <div className="flex flex-col items-center justify-center p-8">
                  <FontAwesomeIcon
                    icon={faSpinner}
                    className="text-blue-500 text-4xl animate-spin mb-4"
                  />
                  <p className="text-gray-600">Loading traffic data...</p>
                </div>
              ) : (
                <>
                  {/* Traffic Data Grid */}
                  <TrafficGridControlTower
                    data={paginatedData}
                    sortConfig={sortConfig}
                    requestSort={requestSort}
                    expandedRows={expandedRows}
                    handleRowClick={handleRowClick}
                    handleViewDetails={handleViewDetails}
                    getOperationTypeText={getOperationTypeText}
                    getTransportIcon={getTransportIcon}
                    currentPage={pagination.currentPage}
                    pageSize={pagination.pageSize}
                  />

                  {/* Add Pagination Component */}
                  <div className="mt-4">
                    <Pagination
                      total={pagination.totalItems}
                      pageSize={pagination.pageSize}
                      currentPage={pagination.currentPage}
                      onPageChange={handlePageChange}
                      onPageSizeChange={handlePageSizeChange}
                    />
                  </div>
                </>
              )}
            </div>

            {/* Shipment Details Panel */}
            {selectedItem && (
              <div
                className="fixed right-0 top-0 bottom-0 w-1/3 z-40 shadow-xl border-l border-gray-200 max-h-full flex flex-col"
                style={{
                  transition: "transform 0.3s ease-in-out",
                  transform: showDetailsPanel
                    ? "translateX(0)"
                    : "translateX(100%)",
                  backgroundColor: "#f9fafb",
                }}
              >
                <div className="p-4 bg-gradient-to-r from-[#1A2654] to-blue-700 border-b border-gray-200 flex justify-between items-center relative">
                  <h2 className="text-lg font-medium text-white">
                    Traffic Details
                  </h2>
                  <div className="flex items-center space-x-3">
                    <img 
                      src={logo} 
                      alt="Wisdom logo" 
                      className="h-8 opacity-90"
                    />
                    <button
                      onClick={() => setShowDetailsPanel(false)}
                      className="text-white hover:text-blue-200"
                    >
                      <FontAwesomeIcon icon={faTimes} />
                    </button>
                  </div>
                </div>

                <div className="p-4 overflow-y-auto flex-1">
                  <TrafficDetails
                    item={selectedItem}
                    getTransportIcon={getTransportIcon}
                    getOperationTypeText={getOperationTypeText}
                  />
                </div>
              </div>
            )}

            {selectedItem && (
              <div
                className="md:hidden fixed inset-0 bg-black bg-opacity-50 z-30"
                onClick={() => setShowDetailsPanel(false)}
              ></div>
            )}
        </div>
      ) : activeTab === "scheduler" ? (
        // Scheduler Tab
        <TrafficScheduler />
      ) : (
        // Schedule Program Tab
        <ScheduleProgram />
      )}
    </div>
  );
};

export default TrafficComponent;
